import React, { useMemo } from 'react'
import {
  BET_TYPE_STATE_UPDATE,
  BETSLIP_STATE_UPDATE,
  BETSLIP_UPDATE_BET_TYPE
} from '../../actions'
import { BET_TYPE, BONUS_TYPE } from '../../constants'
import { useBetslipDispatch, useBetslipState } from './betslip.context'
import { isEmpty } from 'lodash'
import BetManager from '../../managers/bet'
import {
  getBetItemsForMulti,
  getBonusAssignedToBet,
  getIntUID
} from '../../utils'
import { animateOddsInFixedTime } from '../../utils/animation'

const BetTypeContext = React.createContext()

export const BetTypeProvider = (props) => {
  const {
    children,
    betType
  } = props

  const betTypeState = createBetTypeState(betType)

  return (
    <BetTypeContext.Provider value={betTypeState}>
      {children}
    </BetTypeContext.Provider>
  )
}

export const useBetType = () => {
  return React.useContext(BetTypeContext)
}

export function useBetTypeAmountChangeHandler () {
  const { id } = useBetType()
  const dispatch = useBetslipDispatch()

  return React.useCallback(
    amount => dispatch({
      type: BETSLIP_UPDATE_BET_TYPE,
      payload: {
        id,
        amount
      }
    }),
    []
  )
}

function betTypeReducer (state, action) {
  switch (action.type) {
    case BET_TYPE_STATE_UPDATE:
      return {
        ...state,
        ...action.payload
      }
    default:
      return state
  }
}

function createBetTypeInitialState (betType) {
  const requestId = betType.requestId || getIntUID()

  return {
    ...betType,
    requestId,
    synchronized: true,
    loading: true
  }
}

const createBetTypeState = betType => {
  const [state, dispatch] = React.useReducer(
    betTypeReducer,
    betType,
    createBetTypeInitialState
  )

  const betslipDispatch = useBetslipDispatch()
  const {
    fullfited,
    active,
    suspended,
    errors: { [state.requestId]: betError },
    settings,
    betItems,
    bonuses,
    bonusAssignments
  } = useBetslipState()

  const assignedBonusRef = React.useRef(null)

  const notExoticBetItems = useMemo(() => getBetItemsForMulti(betItems), [betItems])

  // const betItems = getSingleBetItems();

  // Recounting bet type odds on betslip outcomes changes
  React.useEffect(
    () => {
      if (isEmpty(betItems)) {
        return
      }

      const active = betItems.every((bet) => bet.active)
      const fullfited = notExoticBetItems.every((bet) => bet.odds)

      if (!active || !fullfited) {
        return
      }

      let payload = {}

      if (state.betType === BET_TYPE.MULTIPLE) {
        payload = BetManager.betTypesCalculator.countMultipleBetType({
          betItems: notExoticBetItems,
          settings
        })
      } else if (state.betType === BET_TYPE.SINGLE) {
        payload = BetManager.betTypesCalculator.countMultipleBetType({
          betItems, settings
        })
      } else {
        payload = BetManager.betTypesCalculator.countSystemBetType({
          betItems,
          // Constants
          winCombinationSize: state.winCombinationSize,
          outcomeAmount: state.outcomeAmount
        })
      }

      payload.loading = false

      dispatch({
        type: BET_TYPE_STATE_UPDATE,
        payload
      })
    },
    [betItems, state.active, fullfited, notExoticBetItems]
  )

  /*
    Bet types statuses based on betslip outcomes statuses
    we need to update own state when betslip betType state recounted
  */
  React.useEffect(
    () => {
      // Updating active & suspended internal state
      dispatch({
        type: BET_TYPE_STATE_UPDATE,
        payload: {
          suspended: suspended,
          active: active
        }
      })
    },
    [suspended, active]
  )

  /*
    Updating betslip state:
    Broadcasting recounted bet type partials to betslip
  */
  React.useEffect(
    () => {
      betslipDispatch({
        type: BETSLIP_UPDATE_BET_TYPE,
        payload: {
          id: state.id,
          requestId: state.requestId,
          odds: state.odds,
          outcomes: state.outcomes,
          active: state.active,
          suspended: state.suspended
        }
      })
    },
    [state.odds, state.active, state.suspended, state.outcomes]
  )

  React.useEffect(
    () => {
      // Initialy setting bet requestId
      // with synchronized true flag
      betslipDispatch({
        type: BETSLIP_UPDATE_BET_TYPE,
        payload: {
          id: state.id,
          requestId: state.requestId,
          synchronized: true
        }
      })
      // Setting bet unsynchronized status
      // On provider unmount we must clean requestId
      return () => {
        betslipDispatch({
          type: BETSLIP_UPDATE_BET_TYPE,
          payload: {
            id: state.id,
            requestId: null,
            synchronized: false,
            active: false
          }
        })
      }
    },
    []
  )

  // Syncing betslip errors to the own state
  React.useLayoutEffect(
    () => {
      dispatch({
        type: BET_TYPE_STATE_UPDATE,
        payload: betError
          ? { errors: betError.violations }
          : { errors: null }
      })
    },
    [betError]
  )

  const isOddsBoostOrPromoOdds = bonus => [BONUS_TYPE.ODDS_BOOST, BONUS_TYPE.PROMO_ODDS].includes(bonus?.bonusType)

  // calculate oddsToDisplay e.g. apply odds boost if it's assigned to this bet
  React.useEffect(() => {
    const { id, requestId, odds, oddsToDisplay } = state
    const assignedBonus = getBonusAssignedToBet({ requestId, bonusAssignments, bonuses })

    if (isOddsBoostOrPromoOdds(assignedBonus)) {
      const { boostedBetOdds } = assignedBonus.props

      if (odds !== assignedBonus.props.betOdds) {
        console.warn('bet odds and odds to boost are different: ', { odds, boostedBetOdds })
      }

      if (boostedBetOdds > odds && boostedBetOdds !== oddsToDisplay) {
        animateOddsInFixedTime({
          start: odds,
          end: boostedBetOdds,
          continueAnimation: () => isOddsBoostOrPromoOdds(assignedBonusRef.current),
          callback: value => {
            if (isOddsBoostOrPromoOdds(assignedBonusRef.current)) { // check if still enabled ODDS_BOOST
              betslipDispatch({
                type: BETSLIP_UPDATE_BET_TYPE,
                payload: {
                  id,
                  oddsToDisplay: value
                }
              })
            } else { // if canceled ODDS_BOOST bonus during animation
              betslipDispatch({
                type: BETSLIP_UPDATE_BET_TYPE,
                payload: {
                  oddsToDisplay: odds
                }
              })
            }
          }
        })
        dispatch({
          type: BET_TYPE_STATE_UPDATE,
          payload: {
            oddsToDisplay: boostedBetOdds
          }
        })
      }
    } else {
      betslipDispatch({
        type: BETSLIP_UPDATE_BET_TYPE,
        payload: {
          id,
          oddsToDisplay: odds
        }
      })
      dispatch({
        type: BET_TYPE_STATE_UPDATE,
        payload: {
          oddsToDisplay: odds
        }
      })
    }
  }, [state.odds, state.requestId, state.oddsToDisplay, bonusAssignments, bonuses])

  // set bet amount if FREEBET bonus is assigned
  React.useEffect(() => {
    const { requestId, amount } = state
    const assignedBonus = getBonusAssignedToBet({ requestId, bonusAssignments, bonuses })

    if (assignedBonus?.bonusType === BONUS_TYPE.FREEBET && amount !== assignedBonus.freeBetAmount) {
      betslipDispatch({
        type: BETSLIP_STATE_UPDATE,
        payload: {
          amount: assignedBonus.freeBetAmount
        }
      })
    }
  }, [state.requestId, state.amount, bonusAssignments, bonuses])

  React.useEffect(
    () => {
      assignedBonusRef.current = getBonusAssignedToBet({ requestId: state.requestId, bonusAssignments, bonuses })
    },
    [bonusAssignments, bonuses, state.requestId]
  )

  return state
}
